
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { MetricCard } from "../analytics/MetricCard";
import { InsightCard } from "../analytics/InsightCard";
import { useRealTimeAnalytics } from "@/hooks/use-real-time-analytics";
import { Industry } from "@/types/assessment";
import { RefreshCcw, BarChart3, Lightbulb, Loader2 } from "lucide-react";

interface RealTimeAnalyticsProps {
  dimensionScores: Record<string, number>;
  overallScore: number;
  industry: Industry;
  organizationName?: string;
}

export const RealTimeAnalytics = ({ 
  dimensionScores, 
  overallScore,
  industry,
  organizationName = ''
}: RealTimeAnalyticsProps) => {
  const { 
    analytics, 
    isLoading, 
    error, 
    refreshAnalytics 
  } = useRealTimeAnalytics({
    dimensionScores,
    overallScore,
    industry,
    organizationName
  });

  if (error) {
    return (
      <Card className="p-6 bg-red-50">
        <h2 className="text-2xl font-bold text-primary mb-4">Real-Time Analytics</h2>
        <div className="text-red-600">
          <p>{error}</p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={refreshAnalytics}
          >
            <RefreshCcw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </div>
      </Card>
    );
  }

  if (isLoading || !analytics) {
    return (
      <Card className="p-6">
        <h2 className="text-2xl font-bold text-primary mb-4">Real-Time Analytics</h2>
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">
            Loading real-time analytics...
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Analyzing your assessment data
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-primary">Real-Time Analytics</h2>
        <Button variant="outline" size="sm" onClick={refreshAnalytics}>
          <RefreshCcw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="mb-6">
        <p className="text-sm text-muted-foreground mb-2">
          Analytics and insights based on your assessment data. 
          These metrics are processed using your assessment scores and industry benchmarks.
        </p>
        <p className="text-xs text-muted-foreground">
          Last updated: {new Date(analytics.lastUpdated).toLocaleString()}
        </p>
        {analytics.comparisonInfo ? (
          <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-100">
            <p className="text-xs text-blue-800">
              <span className="font-medium">Comparison data available:</span> Percentage changes are relative to your assessment from {analytics.comparisonInfo.formattedDate}.
            </p>
          </div>
        ) : (
          <div className="mt-2 p-2 bg-gray-50 rounded border border-gray-100">
            <p className="text-xs text-gray-600">
              <span className="font-medium">No comparison data available.</span> This appears to be the first assessment for this organization.
            </p>
          </div>
        )}
      </div>

      <div className="mb-6 p-4 bg-gray-50 rounded-md border border-gray-100">
        <h3 className="text-sm font-medium mb-2">Understanding Your Metrics</h3>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li><strong>Market Position:</strong> Calculated from strategy and innovation scores, indicating competitive standing.</li>
          <li><strong>AI Readiness:</strong> Calculated from data, talent, and deployment scores, reflecting preparedness for AI adoption.</li>
          <li><strong>Industry Rank:</strong> Your position relative to other organizations in your industry (lower is better).</li>
        </ul>
      </div>

      <Tabs defaultValue="metrics" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="metrics" className="flex items-center">
            <BarChart3 className="h-4 w-4 mr-2" />
            Key Metrics
          </TabsTrigger>
          <TabsTrigger value="insights" className="flex items-center">
            <Lightbulb className="h-4 w-4 mr-2" />
            AI Insights
          </TabsTrigger>
        </TabsList>
        <TabsContent value="metrics">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {analytics.metrics.map((metric) => (
              <MetricCard 
                key={metric.id} 
                metric={metric} 
                comparisonDate={analytics.comparisonInfo?.formattedDate}
              />
            ))}
          </div>
        </TabsContent>
        <TabsContent value="insights">
          <div className="space-y-4">
            {analytics.insights.map((insight) => (
              <InsightCard key={insight.id} insight={insight} />
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <div className="mt-4 text-xs text-muted-foreground border-t pt-4">
        <p>
          Note: These analytics are processed locally based on your assessment data.
          The insights are generated algorithmically and should be considered as suggestions.
        </p>
      </div>
    </Card>
  );
};
